// src/middleware/loadingMiddleware.js
import { startLoading, stopLoading } from '../features/loading/loadingSlice';

// Configuration for different action types and their loading messages
const LOADING_MESSAGES = {
  // Reports
  'reports/fetchReports': 'Loading reports...',
  'reports/fetchReportById': 'Loading report details...',
  'reports/createReport': 'Creating report...',
  'reports/updateReport': 'Updating report...',
  'reports/deleteReport': 'Deleting report...',
  
  // Transactions
  'transactions/fetchTransactions': 'Loading transactions...',
  'transactions/fetchSingleTransaction': 'Loading transaction details...',
  'transactions/updateTransaction': 'Updating transaction...',
  'transactions/verifyTransaction': 'Verifying transaction...',
  
  // Employees
  'employees/fetchEmployees': 'Loading employees...',
  'employees/fetchEmployee': 'Loading employee details...',
  'employees/createEmployee': 'Creating employee...',
  'employees/updateEmployee': 'Updating employee...',
  'employees/deleteEmployee': 'Deleting employee...',
  
  // Stores
  'stores/fetchStores': 'Loading stores...',
  'stores/fetchStore': 'Loading store details...',
  
  // Default message
  default: 'Processing...'
};

// Actions to exclude from global loading (if they have their own loading states)
const EXCLUDED_ACTIONS = new Set([
  // Add action types that should not trigger global loading
  // 'someAction/pending'
]);

const loadingMiddleware = (store) => (next) => (action) => {
  const { type } = action;
  
  // Check if it's a thunk action
  if (type.endsWith('/pending')) {
    const baseActionType = type.replace('/pending', '');
    
    // Skip if this action is excluded
    if (!EXCLUDED_ACTIONS.has(type)) {
      const message = LOADING_MESSAGES[baseActionType] || LOADING_MESSAGES.default;
      
      store.dispatch(startLoading({ 
        actionType: baseActionType, 
        message 
      }));
    }
  }
  
  // Check if it's a fulfilled or rejected thunk action
  if (type.endsWith('/fulfilled') || type.endsWith('/rejected')) {
    const baseActionType = type.replace(/\/(fulfilled|rejected)$/, '');
    
    // Skip if this action is excluded
    if (!EXCLUDED_ACTIONS.has(type.replace(/\/(fulfilled|rejected)$/, '/pending'))) {
      store.dispatch(stopLoading({ actionType: baseActionType }));
    }
  }
  
  return next(action);
};

export default loadingMiddleware;

// Alternative approach: Enhanced thunk middleware
export const createLoadingThunk = (actionType, asyncFunction, loadingMessage) => {
  return (dispatch, getState) => {
    // Start loading
    dispatch(startLoading({ 
      actionType, 
      message: loadingMessage || LOADING_MESSAGES[actionType] || LOADING_MESSAGES.default 
    }));
    
    return asyncFunction(dispatch, getState)
      .finally(() => {
        // Stop loading regardless of success or failure
        dispatch(stopLoading({ actionType }));
      });
  };
};